import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { t } from '../../i18n/nl.js';
import ConfirmModal from '../../components/ConfirmModal';
import CoachAvailabilityCalendar from '../../components/CoachAvailabilityCalendar.jsx';
import { sendPushToClient } from '../../utils/pushUtils.js';
import {
  toAmsterdamISO, amsterdamDateStr, toAmsterdamDate, nowTimeStr,
} from '../../utils/dateUtils.js';

function parseAmsterdamDateTime(isoStr) {
  const d = new Date(isoStr);
  const parts = new Intl.DateTimeFormat('nl-NL', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d);
  const p = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
}

async function updateRecurrenceLessonsWithTime(recurrenceId, metadataUpdates, newTime, fromStartsAt, overrideDetached = false) {
  const now = new Date();
  const lowerBound = (fromStartsAt && new Date(fromStartsAt) > now)
    ? fromStartsAt
    : now.toISOString();

  let query = supabase
    .from('group_lessons')
    .select('id, starts_at')
    .eq('recurrence_id', recurrenceId)
    .eq('is_cancelled', false)
    .gte('starts_at', lowerBound);
  // Individueel afgeweken instanties slaan we standaard over bij een reeks-brede
  // tijdswijziging, tenzij de coach expliciet overschrijven kiest (Onderdeel 1, 2026-07-10).
  if (!overrideDetached) {
    query = query.eq('time_overridden', false);
  }

  const { data: lessons, error } = await query;
  if (error) throw error;
  if (!lessons || lessons.length === 0) return { updated: 0 };

  const { coach_id: _cid, ...safeMeta } = metadataUpdates;

  await Promise.all(lessons.map(lesson => {
    const dateStr = amsterdamDateStr(lesson.starts_at);
    return supabase
      .from('group_lessons')
      .update({ ...safeMeta, starts_at: toAmsterdamISO(dateStr, newTime), time_overridden: false })
      .eq('id', lesson.id)
      .then(({ error: e }) => { if (e) throw e; });
  }));
  return { updated: lessons.length };
}

export default function LesBewerkenPage() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [lesson, setLesson] = useState(null);
  const [loadingInit, setLoadingInit] = useState(true);
  const [editScope, setEditScope] = useState(null); // null = keuze nog niet gemaakt
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [conflictWarning, setConflictWarning] = useState(null);

  // Formuliervelden
  const [naam, setNaam] = useState('');
  const [beschrijving, setBeschrijving] = useState('');
  const [locationId, setLocationId] = useState('');
  const [useCustomLocation, setUseCustomLocation] = useState(false);
  const [customLocation, setCustomLocation] = useState({
    name: '',
    street: '',
    house_number: '',
    postal_code: '',
    city: '',
  });
  const [coachId, setCoachId] = useState('');
  const [datum, setDatum] = useState('');
  const [tijd, setTijd] = useState('');
  const [duur, setDuur] = useState('60');
  const [maxDeelnemers, setMaxDeelnemers] = useState('10');
  const [annuleringstermijn, setAnnuleringstermijn] = useState('24');
  const [creditCost, setCreditCost] = useState('1');

  const [locations, setLocations] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [selectedMembershipTypes, setSelectedMembershipTypes] = useState(new Set());
  const [saveLocConfirmOpen, setSaveLocConfirmOpen] = useState(false);
  const pendingSaveLocRef = useRef(null);
  const [overrideDetached, setOverrideDetached] = useState(false);
  const [creditCostConfirmed, setCreditCostConfirmed] = useState(false);
  const [creditCostWarning, setCreditCostWarning] = useState(null);

  const loadLesson = useCallback(async () => {
    try {
      const [
        { data: les, error: lesErr },
        { data: locs },
        { data: coachs },
        { data: memTypes },
        { data: allowedMemberships },
      ] = await Promise.all([
        supabase.from('group_lessons').select('*, locations(id, name)').eq('id', lessonId).maybeSingle(),
        supabase.from('locations').select('id, name').eq('is_active', true).order('name'),
        supabase.from('profiles').select('id, full_name').in('role', ['coach', 'admin_coach']).is('deleted_at', null).order('full_name'),
        supabase.from('membership_types').select('id, name').eq('is_active', true).order('name'),
        supabase.from('lesson_allowed_memberships').select('membership_type_id').eq('lesson_id', lessonId),
      ]);

      if (lesErr) throw lesErr;
      if (!les) { setError(t('lesBewerken.lesNietGevonden')); setLoadingInit(false); return; }

      setLesson(les);
      setLocations(locs || []);
      setCoaches(coachs || []);
      setMembershipTypes(memTypes || []);

      // Laad de toegestane abonnementstypen voor deze les
      const allowedTypeIds = new Set((allowedMemberships || []).map(m => m.membership_type_id));
      setSelectedMembershipTypes(allowedTypeIds);

      const { date, time } = parseAmsterdamDateTime(les.starts_at);
      setNaam(les.name || '');
      setBeschrijving(les.description || '');

      // Check of custom locatie aanwezig is
      if (les.custom_location_name) {
        setUseCustomLocation(true);
        setLocationId('');
        setCustomLocation({
          name: les.custom_location_name || '',
          street: les.custom_location_street || '',
          house_number: les.custom_location_house_number || '',
          postal_code: les.custom_location_postal_code || '',
          city: les.custom_location_city || '',
        });
      } else {
        setUseCustomLocation(false);
        setLocationId(les.location_id || '');
        setCustomLocation({ name: '', street: '', house_number: '', postal_code: '', city: '' });
      }

      setCoachId(les.executing_coach_id || '');
      setDatum(date);
      setTijd(time);
      setDuur(String(les.duration_minutes || 60));
      setMaxDeelnemers(String(les.max_participants || 10));
      setAnnuleringstermijn(String(les.cancel_deadline_hours ?? 24));
      setCreditCost(String(les.credit_cost ?? 1));

      if (!les.recurrence_id) setEditScope('single');
    } catch (err) {
      setError(t('lesBewerken.foutLaden') + err.message);
    } finally {
      setLoadingInit(false);
    }
  }, [lessonId]);

  useEffect(() => { loadLesson(); }, [loadLesson]);

  async function checkCoachConflicts(coachId, startsAt, durationMinutes, excludeId = null) {
    const endsAt = new Date(new Date(startsAt).getTime() + durationMinutes * 60000).toISOString();
    const { data, error: rpcErr } = await supabase.rpc('check_coach_time_conflicts', {
      p_coach_id: coachId,
      p_starts_at: startsAt,
      p_ends_at: endsAt,
      p_exclude_id: excludeId,
    });
    if (rpcErr) throw rpcErr;
    return data || [];
  }

  async function checkExternalCalendarConflicts(coachId, startsAt, durationMinutes) {
    const endsAt = new Date(new Date(startsAt).getTime() + durationMinutes * 60000).toISOString();
    const { data, error: rpcErr } = await supabase.rpc('check_coach_external_calendar_conflicts', {
      p_coach_id: coachId,
      p_starts_at: startsAt,
      p_ends_at: endsAt,
    });
    if (rpcErr) throw rpcErr;
    return data || [];
  }

  async function gatherBewerkenExternalConflicts() {
    const dur = parseInt(duur);
    const coachToCheck = coachId || lesson.executing_coach_id || lesson.coach_id;

    if (!lesson.recurrence_id || editScope === 'single') {
      return checkExternalCalendarConflicts(coachToCheck, toAmsterdamISO(datum, tijd), dur);
    }

    const now = new Date();
    const lowerBound = editScope === 'from_here' && lesson.starts_at && new Date(lesson.starts_at) > now
      ? lesson.starts_at
      : now.toISOString();

    const { data: lessen, error: lesErr } = await supabase
      .from('group_lessons')
      .select('id, starts_at')
      .eq('recurrence_id', lesson.recurrence_id)
      .eq('is_cancelled', false)
      .gte('starts_at', lowerBound);
    if (lesErr) throw lesErr;
    if (!lessen || lessen.length === 0) return [];

    const results = await Promise.all(lessen.map(l => {
      const dateStr = amsterdamDateStr(l.starts_at);
      return checkExternalCalendarConflicts(coachToCheck, toAmsterdamISO(dateStr, tijd), dur);
    }));

    const seen = new Set();
    const conflicts = [];
    for (const items of results) {
      for (const item of items) {
        if (!seen.has(item.conflict_id)) { seen.add(item.conflict_id); conflicts.push(item); }
      }
    }
    return conflicts;
  }

  function fmtConflictTime(startsAt, endsAt) {
    const fmt = { timeZone: 'Europe/Amsterdam', weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false };
    const endFmt = { timeZone: 'Europe/Amsterdam', hour: '2-digit', minute: '2-digit', hour12: false };
    return `${new Date(startsAt).toLocaleString('nl-NL', fmt)} – ${new Date(endsAt).toLocaleTimeString('nl-NL', endFmt)}`;
  }

  async function gatherBewerkenConflicts() {
    const dur = parseInt(duur);
    // Gebruik de geselecteerde (form-state) coach; als de uitvoerende coach is gewijzigd,
    // controleer dan ook de vorige coach op conflicten.
    const coachToCheck = coachId || lesson.executing_coach_id || lesson.coach_id;
    const prevCoach = lesson.executing_coach_id || lesson.coach_id;
    const coachChanged = coachId && coachId !== prevCoach;

    async function checkSlots(coach) {
      if (!lesson.recurrence_id || editScope === 'single') {
        return checkCoachConflicts(coach, toAmsterdamISO(datum, tijd), dur, lessonId);
      }

      const now = new Date();
      const lowerBound = editScope === 'from_here' && lesson.starts_at && new Date(lesson.starts_at) > now
        ? lesson.starts_at
        : now.toISOString();

      const { data: lessen, error: lesErr } = await supabase
        .from('group_lessons')
        .select('id, starts_at')
        .eq('recurrence_id', lesson.recurrence_id)
        .eq('is_cancelled', false)
        .gte('starts_at', lowerBound);
      if (lesErr) throw lesErr;
      if (!lessen || lessen.length === 0) return [];

      const results = await Promise.all(lessen.map(l => {
        const dateStr = amsterdamDateStr(l.starts_at);
        return checkCoachConflicts(coach, toAmsterdamISO(dateStr, tijd), dur, l.id);
      }));

      const seen = new Set();
      const conflicts = [];
      for (const items of results) {
        for (const item of items) {
          if (!seen.has(item.conflict_id)) { seen.add(item.conflict_id); conflicts.push(item); }
        }
      }
      return conflicts;
    }

    const primary = await checkSlots(coachToCheck);
    if (!coachChanged) return primary;

    // Bij een coachwissel ook de vorige coach checken
    const secondary = await checkSlots(prevCoach);
    const seen = new Set(primary.map(c => c.conflict_id));
    for (const item of secondary) {
      if (!seen.has(item.conflict_id)) { seen.add(item.conflict_id); primary.push(item); }
    }
    return primary;
  }

  async function sendUpdatePushIfNeeded(les, lesNaam, startsAt) {
    const targetIds = new Set();
    if (les.coach_id && les.coach_id !== user?.id) targetIds.add(les.coach_id);
    if (les.executing_coach_id && les.executing_coach_id !== user?.id) targetIds.add(les.executing_coach_id);
    if (targetIds.size === 0) return;

    const isReeks = les.recurrence_id && editScope !== 'single';
    const titel = isReeks ? t('coachPush.reeksGewijzigd') : t('coachPush.lesGewijzigd');
    const body = isReeks
      ? t('coachPush.reeksGewijzigdBody').replace('{naam}', lesNaam)
      : t('coachPush.lesGewijzigdBody').replace('{naam}', lesNaam).replace('{datum}', new Date(startsAt).toLocaleDateString('nl-NL', {
          timeZone: 'Europe/Amsterdam', weekday: 'short', day: 'numeric', month: 'long',
        }));

    for (const targetId of targetIds) {
      try {
        await sendPushToClient(supabase, user.id, targetId, 'lessons_schedule', titel, body);
      } catch (err) {
        // Best-effort per ontvanger: de leswijziging is al opgeslagen en een
        // mislukte push naar coach A mag die naar coach B niet overslaan.
        // Wel loggen — zie de stille 403-regressie van 2026-06-22.
        console.error(`Fout bij versturen wijzigingspush naar ${targetId}:`, err);
      }
    }
  }

  async function checkLessonsHaveEnrollments(lessonIds) {
    // Controleer of er al inschrijvingen zijn op deze lessen
    const { data, error } = await supabase
      .from('lesson_enrollments')
      .select('lesson_id')
      .in('lesson_id', lessonIds)
      .neq('status', 'cancelled');

    if (error) throw error;
    return (data || []).length;
  }

  async function updateLessonAllowedMemberships(lessonIds) {
    // Verwijder bestaande koppelingen voor deze lessen
    await supabase.from('lesson_allowed_memberships').delete().in('lesson_id', lessonIds);

    // Als selectedMembershipTypes leeg is: geen restrictie (alle abonnementen toegestaan)
    if (selectedMembershipTypes.size === 0) return;

    // Controleer of er al inschrijvingen zijn als we een restrictie willen instellen
    const enrollmentCount = await checkLessonsHaveEnrollments(lessonIds);
    if (enrollmentCount > 0) {
      throw new Error(t('lesAanmaken.valInschrijvingenBestaand')
        .replace('{aantal}', enrollmentCount)
        .replace('{lessen}', lessonIds.length));
    }

    // Maak nieuwe koppelingen
    const rows = [];
    lessonIds.forEach(lessonId => {
      selectedMembershipTypes.forEach(typeId => {
        rows.push({ lesson_id: lessonId, membership_type_id: typeId });
      });
    });

    if (rows.length > 0) {
      const { error } = await supabase.from('lesson_allowed_memberships').insert(rows);
      if (error) throw error;
    }
  }

  async function updateRecurrenceAllowedMemberships(recurrenceId, lessonIds) {
    // Verwijder bestaande koppelingen voor deze reeks
    await supabase.from('recurrence_allowed_memberships').delete().eq('recurrence_id', recurrenceId);

    // Als selectedMembershipTypes leeg is: geen restrictie (alle abonnementen toegestaan)
    if (selectedMembershipTypes.size === 0) return;

    // Controleer of er al inschrijvingen zijn op lessen uit deze reeks
    const enrollmentCount = await checkLessonsHaveEnrollments(lessonIds);
    if (enrollmentCount > 0) {
      throw new Error(t('lesAanmaken.valInschrijvingenBestaand')
        .replace('{aantal}', enrollmentCount)
        .replace('{lessen}', lessonIds.length));
    }

    // Maak nieuwe koppelingen
    const rows = Array.from(selectedMembershipTypes).map(typeId => ({
      recurrence_id: recurrenceId,
      membership_type_id: typeId,
    }));

    const { error } = await supabase.from('recurrence_allowed_memberships').insert(rows);
    if (error) throw error;
  }

  async function executeSave() {
    setConflictWarning(null);
    setSaving(true);
    try {
      const metadata = {
        name: naam.trim(),
        description: beschrijving.trim() || null,
        location_id: useCustomLocation ? null : locationId,
        // ONDERDEEL 2 (2026-07-26): nooit terugvallen op NULL. Bij een les die
        // nog geen uitvoerder had is de AANMAKER de feitelijke uitvoerder —
        // dezelfde fallbackketen als de conflictcheck hierboven gebruikt.
        executing_coach_id: coachId || lesson.executing_coach_id || lesson.coach_id,
        duration_minutes: parseInt(duur),
        max_participants: parseInt(maxDeelnemers),
        credit_cost: isWorkshop ? 0 : (parseInt(creditCost) || 0),
        cancel_deadline_hours: parseInt(annuleringstermijn) || 24,
      };

      if (useCustomLocation) {
        metadata.custom_location_name = customLocation.name.trim();
        metadata.custom_location_street = customLocation.street.trim() || null;
        metadata.custom_location_house_number = customLocation.house_number.trim() || null;
        metadata.custom_location_postal_code = customLocation.postal_code.trim() || null;
        metadata.custom_location_city = customLocation.city.trim() || null;
      } else {
        // Wis custom location velden als terug naar vaste locatie
        metadata.custom_location_name = null;
        metadata.custom_location_street = null;
        metadata.custom_location_house_number = null;
        metadata.custom_location_postal_code = null;
        metadata.custom_location_city = null;
      }

      if (!lesson.recurrence_id || editScope === 'single') {
        const { error: updErr } = await supabase
          .from('group_lessons')
          .update({
            ...metadata,
            starts_at: toAmsterdamISO(datum, tijd),
            ...(lesson.recurrence_id ? { time_overridden: true } : {}),
          })
          .eq('id', lessonId);
        if (updErr) throw updErr;
        await updateLessonAllowedMemberships([lessonId]);
      } else {
        // Haal betrokken lesson IDs op
        const now = new Date();
        const lowerBound = editScope === 'from_here' && new Date(lesson.starts_at) > now
          ? lesson.starts_at
          : now.toISOString();

        const { data: affectedLessons } = await supabase
          .from('group_lessons')
          .select('id')
          .eq('recurrence_id', lesson.recurrence_id)
          .eq('is_cancelled', false)
          .gte('starts_at', editScope === 'from_here' ? lowerBound : now.toISOString());

        const lessonIds = (affectedLessons || []).map(l => l.id);

        // Hercontrole vóór reeks-brede metadata-wijziging (Onderdeel 3, 2026-07-10)
        if (metadata.max_participants < lesson.max_participants) {
          const { data: enrolledRows, error: enrErr } = await supabase
            .from('lesson_enrollments')
            .select('lesson_id')
            .in('lesson_id', lessonIds)
            .eq('status', 'enrolled');
          if (enrErr) throw enrErr;
          const counts = {};
          (enrolledRows || []).forEach(r => { counts[r.lesson_id] = (counts[r.lesson_id] || 0) + 1; });
          const overbooked = Object.values(counts).filter(c => c > metadata.max_participants);
          if (overbooked.length > 0) {
            throw new Error(t('lesBewerken.maxParticipantsGuardError')
              .replace('{lessen}', overbooked.length)
              .replace('{inschrijvingen}', overbooked.reduce((s, c) => s + c, 0)));
          }
        }

        if (metadata.credit_cost !== lesson.credit_cost && !creditCostConfirmed) {
          const enrollmentCount = await checkLessonsHaveEnrollments(lessonIds);
          if (enrollmentCount > 0) {
            setSaving(false);
            setCreditCostWarning({ count: enrollmentCount });
            return;
          }
        }

        if (editScope === 'from_here') {
          await updateRecurrenceLessonsWithTime(lesson.recurrence_id, metadata, tijd, lesson.starts_at, overrideDetached);
        } else {
          await updateRecurrenceLessonsWithTime(lesson.recurrence_id, metadata, tijd, null, overrideDetached);
          await updateRecurrenceAllowedMemberships(lesson.recurrence_id, lessonIds);
        }

        // Update lesson allowed memberships voor betrokken lessen
        if (lessonIds.length > 0) {
          await updateLessonAllowedMemberships(lessonIds);
        }
      }

      // Push naar coach als een ander de les heeft bijgewerkt
      await sendUpdatePushIfNeeded(lesson, naam.trim(), toAmsterdamISO(datum, tijd));

      navigate('/coach/rooster', { state: { toast: t('lesBewerken.succesBewerken') } });
    } catch (err) {
      setError(t('lesBewerken.foutOpslaan') + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleSave() {
    setError('');
    setConflictWarning(null);
    setSaving(true);
    try {
      const [conflicts, externalConflicts] = await Promise.all([
        gatherBewerkenConflicts(),
        gatherBewerkenExternalConflicts(),
      ]);
      // Externe agenda-items (ICS-sync) zijn altijd overrulebaar: ze zijn niet
      // per definitie fysieke afspraken (bv. all-day reminders/verjaardagen).
      const softExternal = externalConflicts
        .map(c => ({ ...c, conflict_type: 'external', conflict_title: c.conflict_title || t('conflicten.privateAgendaBusy') }));
      const combinedConflicts = [...conflicts, ...softExternal];
      if (combinedConflicts.length > 0) {
        setSaving(false);
        setConflictWarning({ conflicts: combinedConflicts, onConfirm: executeSave });
        return;
      }
      await executeSave();
    } catch (err) {
      setError(t('conflicten.checkFout') + err.message);
      setSaving(false);
    }
  }

  if (loadingInit) {
    return <div className="loading-center"><div className="spinner" /></div>;
  }

  if (!lesson && error) {
    return (
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <div className="error-msg">{error}</div>
        <button className="btn btn-ghost" onClick={() => navigate('/coach/rooster')} style={{ marginTop: 12 }}>
          {t('lesBewerken.terug')}
        </button>
      </div>
    );
  }

  const isRecurring = !!lesson?.recurrence_id;
  const isWorkshop = lesson?.type === 'workshop';

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <button className="btn btn-ghost" onClick={() => navigate(-1)} style={{ marginBottom: 12 }}>
          {t('lesBewerken.terug')}
        </button>
        <h1 className="page-title">{t('lesBewerken.title')}</h1>
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

      {/* Scopekiezer voor terugkerende les */}
      {isRecurring && editScope === null && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>
            {t('lesBewerken.editScopeLabel')}
          </div>
          {[
            { value: 'single', label: t('lesBewerken.editScopeSingle') },
            { value: 'from_here', label: t('lesBewerken.editScopeFromHere') },
            { value: 'all', label: t('lesBewerken.editScopeAll') },
          ].map((opt, idx) => (
            <button
              key={opt.value}
              onClick={() => setEditScope(opt.value)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                width: '100%', padding: '12px 0',
                background: 'none', border: 'none', cursor: 'pointer',
                borderTop: idx > 0 ? '1px solid var(--color-border)' : 'none',
                textAlign: 'left',
              }}
            >
              <span style={{ fontSize: 14, color: 'var(--color-text)', fontWeight: 500 }}>{opt.label}</span>
              <span style={{ color: 'var(--color-text-muted)', fontSize: 16 }}>›</span>
            </button>
          ))}
        </div>
      )}

      {/* Scope-indicator boven formulier */}
      {isRecurring && editScope !== null && (
        <button
          onClick={() => setEditScope(null)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'var(--color-primary)12', border: '1px solid var(--color-primary)33',
            borderRadius: 6, padding: '6px 12px', cursor: 'pointer', marginBottom: 12,
            fontSize: 13, color: 'var(--color-primary)',
          }}
        >
          <span>🔁</span>
          <span style={{ flex: 1 }}>
            {editScope === 'single' ? t('lesBewerken.editScopeSingle')
              : editScope === 'from_here' ? t('lesBewerken.editScopeFromHere')
              : t('lesBewerken.editScopeAll')}
          </span>
          <span style={{ fontSize: 11 }}>{t('lesFormulier.wijzigen')}</span>
        </button>
      )}

      {/* Override individueel afgeweken instanties — alleen relevant bij reeks-brede tijdswijziging */}
      {isRecurring && (editScope === 'from_here' || editScope === 'all') && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 13, cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={overrideDetached}
            onChange={e => setOverrideDetached(e.target.checked)}
          />
          {t('lesBewerken.overrideDetachedLabel')}
        </label>
      )}

      {/* Formulier — verborgen totdat scope gekozen is bij reeksles */}
      {(editScope !== null) && (
        <>
          <FormSection title={t('lesAanmaken.sectionBasic')}>
            <Field label={t('lesAanmaken.naamLabel')}>
              <input className="form-input" value={naam} onChange={e => setNaam(e.target.value)} />
            </Field>
            <Field label={t('lesAanmaken.beschrijvingLabel')}>
              <textarea
                className="form-input"
                value={beschrijving}
                onChange={e => setBeschrijving(e.target.value)}
                rows={3}
                style={{ resize: 'vertical', fontFamily: 'inherit' }}
              />
            </Field>
            <Field label={t('lesAanmaken.locatieLabel')}>
              <select
                className="form-input"
                value={useCustomLocation ? '__custom__' : locationId}
                onChange={e => {
                  if (e.target.value === '__custom__') {
                    setUseCustomLocation(true);
                    setLocationId('');
                  } else {
                    setUseCustomLocation(false);
                    setLocationId(e.target.value);
                  }
                }}
              >
                <option value="">{t('lesFormulier.selecteerLocatie')}</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                <option value="__custom__">{t('lesAanmaken.andereLocatie')}</option>
              </select>
            </Field>

            {useCustomLocation && (
              <div style={{ marginTop: 16, padding: 16, background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius)', border: '1px solid var(--color-border)' }}>
                <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
                  {t('lesAanmaken.andereLocatieHint')}
                </p>
                <Field label={t('admin.locationName') + ' *'}>
                  <input
                    className="form-input"
                    value={customLocation.name}
                    onChange={e => setCustomLocation(p => ({ ...p, name: e.target.value }))}
                    placeholder={t('admin.locationNamePlaceholder')}
                  />
                </Field>
                <Field label={t('admin.locationStreet')}>
                  <input
                    className="form-input"
                    value={customLocation.street}
                    onChange={e => setCustomLocation(p => ({ ...p, street: e.target.value }))}
                    placeholder={t('admin.locationStreetPlaceholder')}
                  />
                </Field>
                <Field label={t('admin.locationHouseNumber')}>
                  <input
                    className="form-input"
                    value={customLocation.house_number}
                    onChange={e => setCustomLocation(p => ({ ...p, house_number: e.target.value }))}
                    placeholder={t('admin.locationHouseNumberPlaceholder')}
                  />
                </Field>
                <Field label={t('admin.locationPostalCode')}>
                  <input
                    className="form-input"
                    value={customLocation.postal_code}
                    onChange={e => setCustomLocation(p => ({ ...p, postal_code: e.target.value }))}
                    placeholder={t('admin.locationPostalCodePlaceholder')}
                  />
                </Field>
                <Field label={t('admin.locationCity')}>
                  <input
                    className="form-input"
                    value={customLocation.city}
                    onChange={e => setCustomLocation(p => ({ ...p, city: e.target.value }))}
                    placeholder={t('admin.locationCityPlaceholder')}
                  />
                </Field>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => {
                    if (!customLocation.name.trim()) {
                      setError(t('lesAanmaken.valCustomLocationName'));
                      return;
                    }
                    pendingSaveLocRef.current = customLocation;
                    setSaveLocConfirmOpen(true);
                  }}
                  style={{ marginTop: 12 }}
                >
                  {t('lesAanmaken.opslaanAlsVasteLocatie')}
                </button>
              </div>
            )}
            <Field label={t('lesAanmaken.coachLabel')}>
              <select className="form-input" value={coachId} onChange={e => setCoachId(e.target.value)}>
                <option value="">{t('lesFormulier.selecteerCoach')}</option>
                {coaches.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
              </select>
            </Field>
          </FormSection>

          <FormSection title={t('lesAanmaken.sectionDatetime')}>
            {editScope === 'single' && (
              <Field label={t('lesAanmaken.datumLabel')}>
                <input type="date" className="form-input" value={datum} onChange={e => setDatum(e.target.value)} style={{ maxWidth: 200 }} />
              </Field>
            )}
            <Field label={t('lesAanmaken.tijdLabel')}>
              <input type="time" className="form-input" value={tijd} onChange={e => setTijd(e.target.value)} style={{ maxWidth: 160 }} />
            </Field>
            <Field label={t('lesAanmaken.duurLabel')}>
              <input type="number" className="form-input" value={duur} onChange={e => setDuur(e.target.value)} min="1" style={{ maxWidth: 120 }} />
            </Field>
          </FormSection>

          {coachId && (
            <CoachAvailabilityCalendar
              coachId={coachId}
              baseDateStr={datum}
              selectedStartsAt={datum && tijd ? toAmsterdamISO(datum, tijd) : null}
              durationMinutes={parseInt(duur, 10) || 60}
              excludeId={lessonId}
              onSelectSlot={(dateStr, timeStr) => {
                if (editScope === 'single') setDatum(dateStr);
                setTijd(timeStr);
              }}
            />
          )}

          <FormSection title={t('lesAanmaken.sectionCapacity')}>
            <Field label={t('lesAanmaken.maxDeelnemersLabel')}>
              <input type="number" className="form-input" value={maxDeelnemers} onChange={e => setMaxDeelnemers(e.target.value)} min="1" style={{ maxWidth: 120 }} />
            </Field>
            <Field label={t('lesAanmaken.annuleringstermijnLabel')}>
              <input type="number" className="form-input" value={annuleringstermijn} onChange={e => setAnnuleringstermijn(e.target.value)} min="0" style={{ maxWidth: 120 }} />
            </Field>
            {isWorkshop ? (
              <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                {t('lesAanmaken.workshopCreditHint')}
              </p>
            ) : (
              <Field label={t('lesAanmaken.creditCostLabel')}>
                <input type="number" className="form-input" value={creditCost} onChange={e => setCreditCost(e.target.value)} min="0" style={{ maxWidth: 120 }} />
              </Field>
            )}
          </FormSection>

          {/* Toegestane abonnementen */}
          <FormSection title={t('lesAanmaken.sectionAbonnementen')}>
            <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 14 }}>
              {t('lesAanmaken.abonnementenHint')}
            </p>
            {membershipTypes.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                {t('lesAanmaken.geenAbonnementenGevonden')}
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    id="alleAbonnementen"
                    checked={selectedMembershipTypes.size === 0}
                    onChange={e => {
                      if (e.target.checked) setSelectedMembershipTypes(new Set());
                    }}
                    style={{ accentColor: 'var(--color-primary)', width: 16, height: 16, cursor: 'pointer' }}
                  />
                  <label htmlFor="alleAbonnementen" style={{ fontSize: 14, cursor: 'pointer', fontWeight: 600, color: 'var(--color-text)' }}>
                    {t('lesAanmaken.alleAbonnementen')}
                  </label>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {membershipTypes.map(type => {
                    const isSelected = selectedMembershipTypes.has(type.id);
                    return (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => {
                          setSelectedMembershipTypes(prev => {
                            const next = new Set(prev);
                            if (next.has(type.id)) {
                              next.delete(type.id);
                            } else {
                              next.add(type.id);
                            }
                            return next;
                          });
                        }}
                        style={{
                          padding: '8px 14px', borderRadius: 6, border: '1px solid',
                          borderColor: isSelected ? 'var(--color-primary)' : 'var(--color-border)',
                          background: isSelected ? 'var(--color-primary)' : 'transparent',
                          color: isSelected ? '#fff' : 'var(--color-text)',
                          cursor: 'pointer', fontSize: 13, fontWeight: 500,
                        }}
                      >
                        {type.name}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </FormSection>

          {conflictWarning && (
            <div style={{ background: '#fff3e0', border: '1px solid #ff9800', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, marginBottom: 6, color: '#e65100' }}>{t('conflicten.titel')}</div>
              <div style={{ fontSize: 13, marginBottom: 10, color: '#555' }}>{t('conflicten.beschrijving')}</div>
              <ul style={{ margin: '0 0 12px 0', paddingLeft: 20 }}>
                {conflictWarning.conflicts.map(c => (
                  <li key={c.conflict_id} style={{ fontSize: 13, marginBottom: 4, color: 'var(--color-text)' }}>
                    <strong>{c.conflict_type === 'external' ? t('conflicten.typePrivateAgenda') : (c.conflict_type === 'personal' ? t('conflicten.typePT') : t('conflicten.typeGroepsles'))}</strong>
                    {': '}{c.conflict_title} — {fmtConflictTime(c.conflict_starts_at, c.conflict_ends_at)}
                  </li>
                ))}
              </ul>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn"
                  style={{ background: '#c62828', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                  onClick={() => conflictWarning.onConfirm()}
                >
                  {t('conflicten.bevestigenKnop')}
                </button>
                <button className="btn btn-ghost" onClick={() => setConflictWarning(null)}>
                  {t('conflicten.annulerenKnop')}
                </button>
              </div>
            </div>
          )}

          <button
            className="btn"
            onClick={handleSave}
            disabled={saving}
            style={{ width: '100%', padding: '14px', fontSize: 15, marginTop: 8, marginBottom: 32 }}
          >
            {saving ? t('common.laden') : t('lesBewerken.opslaan')}
          </button>
        </>
      )}

      {creditCostWarning && (
        <ConfirmModal
          title={t('lesBewerken.creditCostWaarschuwingTitel')}
          body={t('lesBewerken.creditCostWaarschuwing').replace('{count}', creditCostWarning.count)}
          onConfirm={() => {
            setCreditCostWarning(null);
            setCreditCostConfirmed(true);
            executeSave();
          }}
          onClose={() => setCreditCostWarning(null)}
        />
      )}

      {saveLocConfirmOpen && (
        <ConfirmModal
          title={t('lesAanmaken.opslaanAlsVasteLocatie')}
          body={t('lesAanmaken.opslaanAlsVasteLocatieConfirm')}
          onConfirm={async () => {
            setSaveLocConfirmOpen(false);
            const loc = pendingSaveLocRef.current;
            if (!loc) return;
            try {
              const { data: newLoc, error: err } = await supabase
                .from('locations')
                .insert({
                  name: loc.name.trim(),
                  street: loc.street.trim() || null,
                  house_number: loc.house_number.trim() || null,
                  postal_code: loc.postal_code.trim() || null,
                  city: loc.city.trim() || null,
                })
                .select()
                .maybeSingle();
              if (err) throw err;
              if (!newLoc) throw new Error('Locatie kon niet worden aangemaakt.');
              setLocations(prev => [...prev, newLoc].sort((a, b) => a.name.localeCompare(b.name)));
              setLocationId(newLoc.id);
              setUseCustomLocation(false);
              setCustomLocation({ name: '', street: '', house_number: '', postal_code: '', city: '' });
            } catch (err) {
              setError(t('lesAanmaken.foutOpslaanLocatie') + err.message);
            }
          }}
          onClose={() => setSaveLocConfirmOpen(false)}
        />
      )}
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <div className="card" style={{ marginBottom: 16, padding: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, color: 'var(--color-text)' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 5, fontWeight: 500 }}>
        {label}
      </label>
      {children}
    </div>
  );
}
